// src/components/RifleProfileBuilder.jsx
import React, { useState, useEffect } from 'react'
import { twistCompatibility } from '../lib/ballistics'
import { saveRifleProfile, deleteRifleProfile } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

// ─── Constants ────────────────────────────────────────────────
const CALIBERS = [
  '5.56 NATO',
  '.223 Rem',
  '7mm Rem Mag',
  '.308 Win',
  '.30-06 Springfield',
  '6.5 Creedmoor',
  '.243 Win',
  '.270 Win',
  '.300 Win Mag',
]

const BARREL_LENGTHS = {
  '5.56 NATO':          [10, 12, 14.5, 16, 18, 20, 22, 24],
  '.223 Rem':           [16, 18, 20, 22, 24, 26],
  '7mm Rem Mag':        [20, 22, 24, 26],
  '.308 Win':           [16, 18, 20, 22, 24],
  '.30-06 Springfield': [20, 22, 24],
  '6.5 Creedmoor':      [18, 20, 22, 24, 26],
  '.243 Win':           [20, 22, 24, 26],
  '.270 Win':           [20, 22, 24, 26],
  '.300 Win Mag':       [22, 24, 26],
}

const TWIST_RATES = {
  '5.56 NATO':          ['1:7', '1:8', '1:9', '1:10', '1:12'],
  '.223 Rem':           ['1:7', '1:8', '1:9', '1:10', '1:12', '1:14'],
  '7mm Rem Mag':        ['1:9', '1:9.5', '1:10', '1:11'],
  '.308 Win':           ['1:10', '1:11', '1:11.25', '1:12'],
  '.30-06 Springfield': ['1:10', '1:11', '1:12'],
  '6.5 Creedmoor':      ['1:8', '1:9', '1:10'],
  '.243 Win':           ['1:9', '1:10', '1:12'],
  '.270 Win':           ['1:10'],
  '.300 Win Mag':       ['1:10', '1:11'],
}

const ZERO_DISTANCES = [25, 50, 100, 200, 300]
const SIGHT_HEIGHTS  = [1.5, 1.75, 2.0, 2.25, 2.5, 2.6, 2.75, 3.0]

// ─── Empty profile template ────────────────────────────────────
const emptyProfile = () => ({
  nickname:       '',
  caliber:        '5.56 NATO',
  barrel_length:  16,
  twist_rate:     '1:7',
  sight_height:   2.6,
  zero_distance:  100,
  notes:          '',
})

// ─── Component ────────────────────────────────────────────────
export default function RifleProfileBuilder({ profiles = [], onProfilesChange, onSelect, selectedId }) {
  const { user, isPro } = useAuth()
  const [showForm, setShowForm]   = useState(false)
  const [editProfile, setEdit]    = useState(null)
  const [form, setForm]           = useState(emptyProfile())
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState('')

  const FREE_LIMIT = 2

  const canAddMore = isPro || profiles.length < FREE_LIMIT

  function openNew() {
    setEdit(null)
    setForm(emptyProfile())
    setError('')
    setShowForm(true)
  }

  function openEdit(p) {
    setEdit(p)
    setForm({ ...p })
    setError('')
    setShowForm(true)
  }

  function handleCaliberChange(caliber) {
    const barrels = BARREL_LENGTHS[caliber] || []
    const twists  = TWIST_RATES[caliber]   || []
    setForm(f => ({
      ...f,
      caliber,
      barrel_length: barrels[0] || f.barrel_length,
      twist_rate:    twists[0]  || f.twist_rate,
    }))
  }

  async function handleSave() {
    if (!form.nickname.trim()) { setError('Give your rifle a nickname.'); return }
    if (!user)                 { setError('Sign in to save profiles.');   return }

    setSaving(true)
    setError('')
    const payload = {
      ...form,
      user_id:    user.id,
      updated_at: new Date().toISOString(),
    }
    if (editProfile?.id) payload.id = editProfile.id

    const { error: saveErr } = await saveRifleProfile(payload)
    setSaving(false)
    if (saveErr) { setError(saveErr.message); return }

    setShowForm(false)
    onProfilesChange?.()
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this profile?')) return
    await deleteRifleProfile(id)
    onProfilesChange?.()
  }

  return (
    <div style={styles.container}>

      {/* Profile List */}
      {profiles.length === 0 && !showForm && (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>⊕</div>
          <div style={styles.emptyText}>No rifle profiles yet</div>
          <div style={styles.emptyHint}>Add your first rifle to get started</div>
        </div>
      )}

      {profiles.map(p => {
        const isActive = p.id === selectedId
        return (
          <div
            key={p.id}
            style={{ ...styles.profileCard, ...(isActive ? styles.profileCardActive : {}) }}
            onClick={() => onSelect?.(p)}
          >
            <div style={styles.profileTop}>
              <div>
                <div style={styles.profileName}>{p.nickname}</div>
                <div style={styles.profileCal}>{p.caliber}</div>
              </div>
              <div style={styles.profileActions}>
                <button style={styles.actionBtn} onClick={e => { e.stopPropagation(); openEdit(p) }}>Edit</button>
                <button style={{ ...styles.actionBtn, ...styles.actionBtnDel }}
                  onClick={e => { e.stopPropagation(); handleDelete(p.id) }}>✕</button>
              </div>
            </div>

            <div style={styles.specGrid}>
              <SpecCell label="Barrel"  value={`${p.barrel_length}"`} />
              <SpecCell label="Twist"   value={p.twist_rate} />
              <SpecCell label="Sight Ht" value={`${p.sight_height}"`} />
              <SpecCell label="Zero"    value={`${p.zero_distance} yd`} />
            </div>

            {isActive && (
              <div style={styles.activeBar}>
                <span style={styles.activeDot} />
                ACTIVE PROFILE
              </div>
            )}
          </div>
        )
      })}

      {/* Add Button */}
      {!showForm && (
        canAddMore
          ? <button style={styles.addBtn} onClick={openNew}>+ Add Rifle Profile</button>
          : <div style={styles.limitBanner}>
              <span style={styles.limitText}>Free tier: 2 profiles max</span>
              <button style={styles.upgradeBtn} onClick={() => {}}>Upgrade to Pro →</button>
            </div>
      )}

      {/* Form */}
      {showForm && (
        <div style={styles.form}>
          <div style={styles.formHeader}>
            <span style={styles.formTitle}>{editProfile ? 'Edit Profile' : 'New Rifle Profile'}</span>
            <button style={styles.closeBtn} onClick={() => setShowForm(false)}>✕</button>
          </div>

          {/* Nickname */}
          <FormField label="Nickname">
            <input
              placeholder='e.g. "My AR" or "Deer Gun"'
              value={form.nickname}
              onChange={e => setForm(f => ({ ...f, nickname: e.target.value }))}
            />
          </FormField>

          {/* Caliber */}
          <FormField label="Caliber">
            <select value={form.caliber} onChange={e => handleCaliberChange(e.target.value)}>
              {CALIBERS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FormField>

          {/* Barrel + Twist side by side */}
          <div style={styles.formRow}>
            <FormField label="Barrel Length">
              <select value={form.barrel_length}
                onChange={e => setForm(f => ({ ...f, barrel_length: parseFloat(e.target.value) }))}>
                {(BARREL_LENGTHS[form.caliber] || []).map(b =>
                  <option key={b} value={b}>{b}"</option>
                )}
              </select>
            </FormField>
            <FormField label="Twist Rate">
              <select value={form.twist_rate}
                onChange={e => setForm(f => ({ ...f, twist_rate: e.target.value }))}>
                {(TWIST_RATES[form.caliber] || []).map(t =>
                  <option key={t} value={t}>{t}</option>
                )}
              </select>
            </FormField>
          </div>

          {/* Twist compatibility hint */}
          <TwistHint caliber={form.caliber} twist={form.twist_rate} />

          {/* Sight Height + Zero side by side */}
          <div style={styles.formRow}>
            <FormField label='Sight Height Over Bore'>
              <select value={form.sight_height}
                onChange={e => setForm(f => ({ ...f, sight_height: parseFloat(e.target.value) }))}>
                {SIGHT_HEIGHTS.map(h =>
                  <option key={h} value={h}>{h}"</option>
                )}
              </select>
              <div style={styles.hint}>Scope centerline to bore centerline</div>
            </FormField>
            <FormField label="Zero Distance">
              <select value={form.zero_distance}
                onChange={e => setForm(f => ({ ...f, zero_distance: parseInt(e.target.value) }))}>
                {ZERO_DISTANCES.map(z =>
                  <option key={z} value={z}>{z} yds</option>
                )}
              </select>
            </FormField>
          </div>

          {/* Notes */}
          <FormField label="Notes (optional)">
            <textarea
              placeholder="Make, model, optic, any other notes..."
              value={form.notes}
              rows={2}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              style={{ resize: 'none' }}
            />
          </FormField>

          {error && <div style={styles.errorMsg}>{error}</div>}

          <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editProfile ? 'Update Profile' : 'Save Profile'}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────
function SpecCell({ label, value }) {
  return (
    <div style={styles.specCell}>
      <div style={styles.specLabel}>{label}</div>
      <div style={styles.specValue}>{value}</div>
    </div>
  )
}

function FormField({ label, children }) {
  return (
    <div style={styles.formField}>
      <div style={styles.fieldLabel}>{label}</div>
      {children}
    </div>
  )
}

function TwistHint({ caliber, twist }) {
  // Show common weight ranges per twist for 5.56/.223
  if (!caliber.includes('5.56') && !caliber.includes('.223')) return null
  const rate = parseFloat(twist.replace('1:', ''))
  const ranges = {
    7:  { range: '62–77gr', note: 'Optimized for heavy match & hunting bullets' },
    8:  { range: '55–77gr', note: 'Versatile — widest compatible range' },
    9:  { range: '55–69gr', note: 'Good for standard 55gr loads' },
    10: { range: '45–60gr', note: 'Light bullets only' },
    12: { range: '40–55gr', note: 'Varmint/light bullets only' },
  }
  const info = ranges[rate]
  if (!info) return null
  return (
    <div style={styles.twistHint}>
      <span style={styles.twistBadge}>{twist}</span>
      <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
        Optimal: <b style={{ color: 'var(--accent)' }}>{info.range}</b> — {info.note}
      </span>
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────
const styles = {
  container:   { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px' },
  empty:       { textAlign: 'center', padding: '40px 20px' },
  emptyIcon:   { fontSize: 32, color: 'var(--accent)', marginBottom: 12 },
  emptyText:   { fontSize: 15, color: 'var(--text-secondary)', marginBottom: 6 },
  emptyHint:   { fontSize: 13, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace' },

  profileCard: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)', padding: '14px 16px', cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  profileCardActive: { borderColor: 'var(--accent)', background: 'var(--bg-tertiary)' },
  profileTop:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  profileName: { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' },
  profileCal:  { fontSize: 11, color: 'var(--accent)', fontFamily: 'Courier New, monospace', marginTop: 2 },
  profileActions: { display: 'flex', gap: 6 },
  actionBtn:   {
    background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
    borderRadius: 5, color: 'var(--text-secondary)', fontSize: 11,
    padding: '4px 10px', cursor: 'pointer', fontFamily: 'Courier New, monospace'
  },
  actionBtnDel: { color: 'var(--red)', borderColor: 'rgba(224,92,92,0.2)' },

  specGrid:  { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 },
  specCell:  { background: 'var(--bg-tertiary)', borderRadius: 6, padding: '7px 8px' },
  specLabel: { fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Courier New, monospace' },
  specValue: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'Courier New, monospace', marginTop: 2 },

  activeBar: {
    display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
    fontSize: 10, letterSpacing: '0.1em', color: 'var(--accent)',
    fontFamily: 'Courier New, monospace'
  },
  activeDot: {
    width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)',
    animation: 'pulse 2s infinite'
  },

  addBtn: {
    background: 'transparent', border: '1px dashed var(--border)',
    borderRadius: 'var(--radius-md)', color: 'var(--accent)',
    padding: '12px', fontSize: 13, width: '100%',
    fontFamily: 'Courier New, monospace', letterSpacing: '0.04em',
    transition: 'border-color 0.2s, background 0.2s',
  },

  limitBanner: {
    background: 'var(--accent-dim)', border: '1px solid var(--accent-border)',
    borderRadius: 'var(--radius-md)', padding: '12px 16px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  limitText:   { fontSize: 12, color: 'var(--accent)', fontFamily: 'Courier New, monospace' },
  upgradeBtn:  {
    background: 'var(--accent)', color: '#0a0a0a', border: 'none',
    borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer'
  },

  form: {
    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: 14
  },
  formHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  formTitle:   { fontSize: 14, fontWeight: 600, color: 'var(--accent)' },
  closeBtn:    { background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer' },
  formRow:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  formField:   { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel:  { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'Courier New, monospace' },
  hint:        { fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Courier New, monospace', marginTop: 4 },

  twistHint: {
    background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
    borderRadius: 6, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10
  },
  twistBadge: {
    background: 'var(--accent-dim)', border: '1px solid var(--accent-border)',
    color: 'var(--accent)', fontFamily: 'Courier New, monospace', fontSize: 11,
    padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap'
  },

  errorMsg: { fontSize: 12, color: 'var(--red)', fontFamily: 'Courier New, monospace', padding: '8px 12px', background: 'rgba(224,92,92,0.08)', borderRadius: 6 },

  saveBtn: {
    background: 'var(--accent)', color: '#0a0a0a', border: 'none',
    borderRadius: 8, padding: '13px', fontSize: 14, fontWeight: 700,
    width: '100%', letterSpacing: '0.04em',
    opacity: 1, transition: 'opacity 0.2s'
  },
}
